import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../api/client";
import { IconLoader, IconAlert, IconTrash, IconCheck, IconPlus } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

interface CountItem {
  id: string;
  product_id: string;
  product_name: string;
  product_code: string | null;
  unit: string;
  category_id: string | null;
  category_name: string | null;
  system_qty: number;
  counted_qty: number | null;
  diff: number | null;
  unit_cost: number | null;
  value_diff: number | null;
}

interface StockCount {
  id: string;
  warehouse_id: string;
  warehouse_name: string;
  status: string;
  note: string | null;
  started_at: string;
  finished_at: string | null;
  items: CountItem[];
  total_value_diff: number | null;
  missing_price_count: number;
}

interface EligibleProduct { id: string; name: string; code: string | null; unit: string; category_id: string | null; }
interface Category { id: string; name: string; }

const fmtQty = (v: number | null) => (v === null ? "—" : String(v));
const fmtMoney = (v: number | null) => (v === null ? "—" : `${(v / 100).toFixed(2)} Kč`);
const fmt = (d: string | null) => (d ? new Date(d).toLocaleString("cs-CZ") : "—");
const diffColor = (d: number | null) => (d === null || d === 0 ? "var(--muted)" : d < 0 ? "var(--red)" : "var(--green)");

export default function StockCountDetail() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [count, setCount] = useState<StockCount | null>(null);
  const [products, setProducts] = useState<EligibleProduct[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [step, setStep] = useState<"count" | "review">("count");
  const [groupByCat, setGroupByCat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isOpen = count?.status === "open";

  async function load() {
    try {
      const { data } = await api.get<StockCount>(`/stock-counts/${id}`);
      setCount(data);
      setNote(data.note || "");
      // Merge server values but keep local in-flight edits — saving one
      // row must not wipe what the user is typing in another row.
      setCounts(prev => ({
        ...Object.fromEntries(data.items.map(i => [i.id, i.counted_qty === null ? "" : String(i.counted_qty)])),
        ...prev,
      }));
    } catch (e) {
      setErr(apiError(e, t("stockCount.loadError")));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    api.get<EligibleProduct[]>(`/stock-counts/${id}/products`).then(({ data }) => setProducts(data)).catch(() => {});
    api.get<Category[]>("/catalog/categories").then(({ data }) => setCategories(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addedIds = useMemo(() => new Set((count?.items || []).map(i => i.product_id)), [count]);

  async function run(fn: () => Promise<any>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setErr(apiError(e, t("stockCount.saveError")));
    } finally {
      setBusy(false);
    }
  }

  const addProduct = (productId: string) => run(() => api.post(`/stock-counts/${id}/items`, { product_id: productId }));
  const addCategory = (catId: string) => run(() => api.post(`/stock-counts/${id}/items/bulk`, { scope: "category", category_id: catId }));
  const addAll = () => run(() => api.post(`/stock-counts/${id}/items/bulk`, { scope: "all" }));
  const removeItem = (itemId: string) => run(() => api.delete(`/stock-counts/${id}/items/${itemId}`));

  const parsedQty = (raw: string | undefined) =>
    raw === "" || raw === undefined ? null : parseFloat(String(raw).replace(",", "."));

  async function saveCounted(itemId: string) {
    const item = count?.items.find(i => i.id === itemId);
    if (!item) return;
    const value = parsedQty(counts[itemId]);
    if (value !== null && (isNaN(value) || value < 0)) { setErr(t("stockCount.invalidQty")); return; }
    if (value === item.counted_qty) return;
    // Background save — don't block the UI (busy) while typing/tabbing.
    try {
      await api.put(`/stock-counts/${id}/items/${itemId}`, { counted_qty: value });
      setCount(prev => prev ? { ...prev, items: prev.items.map(x => x.id === itemId ? { ...x, counted_qty: value } : x) } : prev);
    } catch (e) {
      setErr(apiError(e, t("stockCount.saveError")));
    }
  }

  // Persist any edited-but-unsaved quantities (e.g. the user clicks the
  // review button straight from a field, before blur saves run).
  async function flushPendingCounts() {
    if (!count) return;
    const pending = count.items.filter(i => {
      const v = parsedQty(counts[i.id]);
      return v !== null && !isNaN(v) && v !== i.counted_qty;
    });
    await Promise.all(pending.map(i => api.put(`/stock-counts/${id}/items/${i.id}`, { counted_qty: parsedQty(counts[i.id]) })));
  }

  async function saveNote() {
    if (!count || note === (count.note || "")) return;
    await run(() => api.patch(`/stock-counts/${id}`, { note: note || null }));
  }

  async function toReview() {
    setErr(null);
    setBusy(true);
    try {
      await flushPendingCounts();
      await load();
      setStep("review");
    } catch (e) {
      setErr(apiError(e, t("stockCount.saveError")));
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.post<StockCount>(`/stock-counts/${id}/finish`);
      setCount(data);
      setStep("review");
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setErr(detail === "MISSING_COUNTED_QTY" ? t("stockCount.missingCountedAll") : (detail || t("stockCount.saveError")));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(t("stockCount.deleteConfirm"))) return;
    try {
      await api.delete(`/stock-counts/${id}`);
      nav("/stock-counts");
    } catch (e: any) {
      setErr(apiError(e, t("stockCount.saveError")));
    }
  }

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;
  if (!count) return <div className="card" style={{ padding: 24, color: "var(--red)" }}>{err || t("stockCount.loadError")}</div>;

  const items = count.items;
  const totalDiff = items.reduce((s, i) => s + (i.value_diff ?? 0), 0);

  // Alphabetical always; optionally grouped by category (each group alpha).
  const collator = new Intl.Collator("cs");
  const sorted = [...items].sort((a, b) =>
    groupByCat
      ? collator.compare(a.category_name || "￿", b.category_name || "￿") || collator.compare(a.product_name, b.product_name)
      : collator.compare(a.product_name, b.product_name)
  );
  const displayItems = sorted.map((item, idx) => ({
    item,
    newGroup: groupByCat && (idx === 0 || sorted[idx - 1].category_name !== item.category_name),
  }));
  const groupHeader = (i: CountItem, colspan: number) => (
    <tr key={`cat-${i.id}`}>
      <td colSpan={colspan} style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)", paddingTop: 14 }}>
        {i.category_name || t("stockCount.uncategorized")}
      </td>
    </tr>
  );

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{isOpen ? t("stockCount.detailTitleOpen") : t("stockCount.detailTitleDone")}</h1>
          <p>
            {count.warehouse_name} · {t("stockCount.startedAt")}: {fmt(count.started_at)}
            {count.finished_at && <> · {t("stockCount.finishedAt")}: {fmt(count.finished_at)}</>}
          </p>
        </div>
        <Link to="/stock-counts" className="ghost">{t("stockCount.backToList")}</Link>
      </div>

      {err && <div className="card" style={{ padding: 12, marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}><IconAlert size={16} /> {err}</div>}

      {isOpen && step === "count" && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <div style={{ marginBottom: 16, maxWidth: 480 }}>
            <label>{t("stockCount.note")}</label>
            <input value={note} onChange={e => setNote(e.target.value)} onBlur={saveNote} placeholder={t("stockCount.notePlaceholder")} />
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label>{t("stockCount.addProduct")}</label>
              <SearchableSelect
                value=""
                onChange={val => { if (val) void addProduct(val); }}
                options={products.filter(p => !addedIds.has(p.id)).map(p => ({ value: p.id, label: `${p.name}${p.code ? ` (${p.code})` : ""}` }))}
                placeholder={t("stockCount.addProduct")}
                searchPlaceholder={t("stockCount.searchProduct")}
              />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label>{t("stockCount.selectCategory")}</label>
              <SearchableSelect
                value=""
                onChange={val => { if (val) void addCategory(val); }}
                options={categories
                  .filter(c => products.some(p => p.category_id === c.id && !addedIds.has(p.id)))
                  .map(c => ({ value: c.id, label: c.name }))}
                placeholder={t("stockCount.selectCategory")}
                searchPlaceholder={t("stockCount.searchCategory")}
              />
            </div>
            <button className="ghost" onClick={addAll} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <IconPlus size={16} /> {t("stockCount.addAll")}
            </button>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", fontSize: 13 }}>
          <input type="checkbox" checked={groupByCat} onChange={e => setGroupByCat(e.target.checked)} />
          {t("stockCount.groupByCategory")}
        </label>
      )}
      <div className="card" style={{ overflow: "auto", marginBottom: 24 }}>
        {items.length === 0 ? (
          <p style={{ color: "var(--muted)", padding: 24 }}>{t("stockCount.noItems")}</p>
        ) : step === "count" && isOpen ? (
          <table>
            <thead>
              <tr>
                <th>{t("stockCount.product")}</th>
                <th>{t("stockCount.category")}</th>
                <th style={{ textAlign: "right" }}>{t("stockCount.systemQty")}</th>
                <th>{t("stockCount.countedQty")}</th>
                <th style={{ textAlign: "right" }}>{t("stockCount.diff")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(({ item: i, newGroup }) => {
                const raw = counts[i.id];
                const counted = raw === "" || raw === undefined ? null : parseFloat(String(raw).replace(",", "."));
                const diff = counted === null || isNaN(counted) ? null : counted - i.system_qty;
                return (
                  <Fragment key={i.id}>
                    {newGroup && groupHeader(i, 6)}
                    <tr>
                    <td>{i.product_name}{i.product_code ? ` (${i.product_code})` : ""}</td>
                    <td style={{ color: "var(--muted)" }}>{i.category_name || "—"}</td>
                    <td style={{ textAlign: "right" }}>{i.system_qty} {i.unit}</td>
                    <td>
                      <input
                        type="number" step="0.001" min="0"
                        value={raw ?? ""}
                        onChange={e => setCounts(prev => ({ ...prev, [i.id]: e.target.value }))}
                        onBlur={() => void saveCounted(i.id)}
                        style={{ width: 110, marginRight: 6 }}
                        placeholder="—"
                      />{i.unit}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: diffColor(diff) }}>
                      {diff === null ? "—" : `${diff > 0 ? "+" : ""}${diff} ${i.unit}`}
                    </td>
                    <td><button type="button" tabIndex={-1} className="ghost" onClick={() => removeItem(i.id)} style={{ color: "var(--red)" }}><IconTrash size={16} /></button></td>
                  </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t("stockCount.product")}</th>
                <th>{t("stockCount.category")}</th>
                <th style={{ textAlign: "right" }}>{t("stockCount.beforeQty")}</th>
                <th style={{ textAlign: "right" }}>{t("stockCount.afterQty")}</th>
                <th style={{ textAlign: "right" }}>{t("stockCount.diff")}</th>
                <th style={{ textAlign: "right" }}>{t("stockCount.unitCost")}</th>
                <th style={{ textAlign: "right" }}>{t("stockCount.valueDiff")}</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(({ item: i, newGroup }) => (
                <Fragment key={i.id}>
                  {newGroup && groupHeader(i, 7)}
                  <tr>
                  <td>{i.product_name}{i.product_code ? ` (${i.product_code})` : ""}</td>
                  <td style={{ color: "var(--muted)" }}>{i.category_name || "—"}</td>
                  <td style={{ textAlign: "right" }}>{i.system_qty} {i.unit}</td>
                  <td style={{ textAlign: "right" }}>{fmtQty(i.counted_qty)} {i.unit}</td>
                  <td style={{ textAlign: "right", fontWeight: 600, color: diffColor(i.diff) }}>
                    {i.diff === null ? "—" : `${i.diff > 0 ? "+" : ""}${i.diff} ${i.unit}`}
                  </td>
                  <td style={{ textAlign: "right", color: i.unit_cost === null ? "var(--red)" : undefined }}>
                    {i.unit_cost === null ? t("stockCount.priceMissing") : fmtMoney(i.unit_cost)}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 600, color: diffColor(i.value_diff) }}>{fmtMoney(i.value_diff)}</td>
                </tr>
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} style={{ textAlign: "right", fontWeight: 600 }}>{t("stockCount.totalDiff")}</td>
                <td style={{ textAlign: "right", fontWeight: 700, fontSize: 16, color: diffColor(totalDiff) }}>{fmtMoney(totalDiff)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {step === "review" && items.filter(i => i.counted_qty === null).length > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 24, background: "var(--accent-dim)", color: "var(--accent)", display: "flex", alignItems: "center", gap: 8 }}>
          <IconAlert size={16} /> {t("stockCount.missingCounted", { count: items.filter(i => i.counted_qty === null).length })}
        </div>
      )}

      {count.missing_price_count > 0 && (step === "review" || !isOpen) && (
        <div className="card" style={{ padding: 12, marginBottom: 24, background: "var(--accent-dim)", color: "var(--accent)", display: "flex", alignItems: "center", gap: 8 }}>
          <IconAlert size={16} /> {t("stockCount.missingPrice", { count: count.missing_price_count })}
        </div>
      )}

      {isOpen && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {step === "count" ? (
            <>
              <button className="primary" onClick={toReview} disabled={busy || items.length === 0}>
                {t("stockCount.toReview")}
              </button>
              <button className="ghost" onClick={remove} disabled={busy} style={{ color: "var(--red)" }}>
                {t("stockCount.deleteCount")}
              </button>
            </>
          ) : (
            <>
              <button className="ghost" onClick={() => setStep("count")} disabled={busy}>
                {t("stockCount.backToCount")}
              </button>
              <button className="primary" onClick={finish} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <IconCheck size={18} /> {busy ? t("common.saving") : t("stockCount.finish")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
